import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { ApolloLink } from 'apollo-link'
import { setContext } from 'apollo-link-context'

import introspectionResult from '~/fragmentTypes.json'
import { createDraftInjectorLink } from '~/apollo/links/draft-injector'

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: introspectionResult
})

/**
 * Apollo client factory for @nuxtjs/apollo.
 *
 * Link chain (order matters — first link sees the operation first):
 *
 *   1. previewContextLink   — server only. Reads the preview Vuex state for
 *                             the current SSR request and stamps the Apollo
 *                             operation context with { isPreview, draftsToken }.
 *                             Has no effect in the browser bundle.
 *
 *   2. draftInjectorLink    — AST-rewrites top-level allowlisted Query fields
 *                             to include `status: DRAFT` when isPreview is
 *                             true. Allowlist is the build-time-generated
 *                             snapshot at apollo/draft-fields.generated.json.
 *
 *   3. authHeaderLink       — server only. When isPreview is true, attaches
 *                             `Authorization: Bearer <STRAPI_DRAFTS_TOKEN>`
 *                             so Strapi accepts the DRAFT query (drafts are
 *                             not exposed to anonymous public traffic).
 *
 *   (The terminating network link is NOT added here — @nuxtjs/apollo appends
 *    its own upload-capable HttpLink from `httpEndpoint`; see the return below.)
 *
 * Why context, not closure: @nuxtjs/apollo creates one ApolloClient per SSR
 * request, but during a request the same operation flows through every link.
 * Stashing per-request state on `operation.setContext({ ... })` keeps it
 * thread-safe even if a future refactor shares clients.
 *
 * Browser safety: the Bearer header is only added when `process.server` is
 * true. The drafts token lives in `privateRuntimeConfig` and is therefore
 * never bundled into the client JS. On the client, the Apollo cache is
 * hydrated from the SSR payload, so the editor sees draft data without the
 * browser ever talking directly to Strapi for that query.
 */
export default ({ req, app, store }) => {
  // SSR uses the in-cluster Service DNS (fast, no TLS hop, no public DNS dep).
  // Browser must use the public hostname from publicRuntimeConfig.cmsURL.
  const endpoint = process.server
    ? (process.env.STRAPI_URL_INTERNAL || app.$config.cmsURL)
    : app.$config.cmsURL
  const uri = endpoint + '/graphql'

  // ---- Link 1: thread preview state onto the operation context (SSR only)
  const previewContextLink = setContext((_operation, prevContext) => {
    if (!process.server) return prevContext
    const isPreview = !!(store && store.getters && store.getters['preview/isPreview'])
    if (!isPreview) return prevContext
    const draftsToken =
      (app && app.$config && app.$config.strapiDraftsToken) ||
      process.env.STRAPI_DRAFTS_TOKEN ||
      ''
    return {
      ...prevContext,
      isPreview: true,
      draftsToken
    }
  })

  // ---- Link 2: AST-inject `status: DRAFT` when context says preview is on
  const draftInjectorLink = createDraftInjectorLink({
    isPreviewRequest: (operation) => {
      const ctx = operation.getContext()
      return !!(ctx && ctx.isPreview)
    }
  })

  // ---- Link 3: stamp Bearer header when context says preview is on (SSR only)
  const authHeaderLink = setContext((_operation, prevContext) => {
    if (!process.server) return prevContext
    if (!prevContext || !prevContext.isPreview || !prevContext.draftsToken) {
      return prevContext
    }
    const prevHeaders = (prevContext && prevContext.headers) || {}
    return {
      ...prevContext,
      headers: {
        ...prevHeaders,
        Authorization: 'Bearer ' + prevContext.draftsToken
      }
    }
  })

  // `link` is MIDDLEWARE-ONLY here — deliberately NOT terminating. @nuxtjs/apollo
  // builds its own (upload-capable) terminating HttpLink from `httpEndpoint` and
  // appends it: `ApolloLink.from([link, httpLink])`. If `link` ended in its own
  // terminating HttpLink, that append would drop the module's link and log
  // "You are calling concat on a terminating link, which will have no effect"
  // on every SSR request. Leaving the http terminator to the module both
  // silences that warning and routes requests through its upload-capable link.
  return {
    httpEndpoint: uri,
    link: ApolloLink.from([
      previewContextLink,
      draftInjectorLink,
      authHeaderLink
    ]),
    cache: new InMemoryCache({ fragmentMatcher })
  }
}
